import { useMemo, useState } from "react";
import { dayKeyOf, shiftDayKey } from "../lib/days";
import { useCoverageWeek } from "./useCoverageWeek";
import { coverageDeferralNote } from "../lib/deferrals";
import {
  matrix, summarise, byJoint, servable, auditSetCanFindAHole, cellKey, stateLabel,
  type CellReading,
} from "../lib/coverage";
import { CELL_COUNT, MINIMUM_WEIGHTED_SETS } from "../lib/coverageVocabulary";

/* The coverage matrix — step 3 of `docs/JOINT-ACTION-ENGINE.md` §8, and his own instruction for
 * it: *"Build the coverage matrix view for a single athlete-week. Ship it as a coach tool BEFORE
 * wiring it into generation."*
 *
 * ⚠️ READ-ONLY, AND STRUCTURALLY SO. There is no control on this screen that writes anything, and
 * nothing in the generator reads the table behind it — his §8 puts that at step 5 and warns
 * against attempting steps 1–5 in one pass. A harness ruler asserts the second half.
 *
 * ⚠️ AND IT SAYS WHAT IT DOES NOT KNOW, WHICH IS THE HALF THAT MAKES IT USABLE. A matrix that
 * printed a confident ZERO over undescribed exercises would fail in the FLATTERING direction — a
 * screen full of them looks exactly like a working audit — so the rule reports `provisional` and
 * this screen leads with the completeness line rather than burying it. All 74 exercises of this
 * athlete's week are now described, so his zeros ARE findings; the next athlete's will not be.
 */

/* ⚠️ THE LOADER MOVED OUT AND IS NOW SHARED WITH THE GOAL-GAP REPORT — see
 * `routes/useCoverageWeek.ts`. Two tabs of ONE page may not disagree about what a week is or
 * what "the library" means; a second copy of the query is that disagreement waiting to happen.
 *
 * 🔴 AND MOVING IT CHANGED WHAT THIS SCREEN CAN SEE. The old query read `exercise_coverage`
 * filtered to the week's own exercises, which was right while the only described exercises WERE
 * one athlete's week — and is exactly what §11.3 measured as making the audit set structurally
 * unable to report a hole: servable ∩ empty is empty by construction when the library IS the
 * week. §12.6 closed that by describing all 108 coach exercises, and the hook reads the whole
 * table, so `auditSetCanFindAHole` can finally answer yes and the filter below can narrow.
 */

export function ClientCoverage({ clientId, today }: { clientId: string; today: Date }) {
  // ⚠️ ONLY the accidental zeros are worth expanding by default; everything else is noise on a
  // screen whose whole point is finding the empty cells.
  const [showAll, setShowAll] = useState(false);

  // "The last seven days" is today and the six before it — `client.ts`'s own convention, reused
  // rather than restated, so this screen and the standing card cannot disagree about a week.
  const from = shiftDayKey(today, -6);
  const to = dayKeyOf(today);
  const state = useCoverageWeek(clientId, from, to);

  const view = useMemo(() => {
    if (state.status !== "ready") return null;
    const readings = matrix(state.input);
    const audit = servable(state.input.rows);
    // 🔴 ASKED BEFORE THE FILTER IS APPLIED, AND THE ANSWER DECIDES WHETHER IT MAY BE. The audit
    // set can only report a cell that is servable AND empty; while the described library is no
    // wider than the week, that intersection is empty by construction and filtering on it hides
    // every finding this screen has.
    const auditNarrows = auditSetCanFindAHole(state.input);
    return { readings, audit, auditNarrows, sum: summarise(readings, state.input) };
  }, [state]);

  if (state.status === "loading") {
    return <p className="ht-callout loading-line" aria-live="polite">Reading the week…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="client-error" role="alert">
        <p className="ht-body">Could not read this week&rsquo;s coverage.</p>
        <p className="ht-callout">{state.message}</p>
      </div>
    );
  }
  if (view === null) return null;
  const { readings, audit, auditNarrows, sum } = view;
  /* The note the two coach screens print about the declarations is built in `deferrals.ts` and
     executed by `deferrals.test.ts` — a sentence assembled in a component is the one fallible
     thing here with no runner, and the first cut of this one shipped a paragraph beginning with
     a lowercase word for exactly that reason. */
  const deferralNote = ` ${coverageDeferralNote(state.deferrals, dayKeyOf(today))}`;

  // What is worth showing: the audit set — cells at least one described exercise can fill — plus
  // anything the week has actually put weight into. The rest is a LIBRARY gap rather than a hole
  // in the athlete, and printing 118 rows of it is the wall of warnings nobody reads.
  const inAudit = (r: CellReading) => audit.has(cellKey(r.action.id, r.zone)) || r.weightedSets > 0;
  // ⚠️ THE FILTER IS ONLY APPLIED WHEN IT CAN EXCLUDE SOMETHING THE WEEK DOES NOT ALREADY TRAIN.
  // Otherwise every cell it keeps is a cell with weight in it, so the screen would show only what
  // is going well and none of what is missing — and the counts list above would print a number of
  // holes nobody could reach.
  const shownReadings = showAll || !auditNarrows ? readings : readings.filter(inAudit);
  const groups = byJoint(shownReadings);

  return (
    <section className="coverage" aria-labelledby="coverage-h">
      <h2 id="coverage-h" className="ht-headline">What this week trains, by joint action and range</h2>

      {/* ⚠️ THE METHOD MOVED UNDER A DISCLOSURE AND NOTHING WAS DELETED — the guess is still
          admitted, in full, one click away. It LED the page before this, so a coach met three
          lines of method before a single number on every visit; the completeness line below is
          the one that must lead, because everything on the page is read through it. */}
      <details className="ht-disclosure coverage-intro">
        <summary className="ht-caption">How this is counted</summary>
        <p className="ht-caption">
          The prime mover changes across a joint&rsquo;s range, so covering an action is not
          covering its zones. A cell counts role-weighted sets: a prime mover earns a whole set, a
          synergist half, a stabiliser a quarter. {MINIMUM_WEIGHTED_SETS} a week is the minimum
          — <strong>and that number is a guess</strong>, not something any source states.
        </p>
      </details>

      {/* 🔴 THE COMPLETENESS LINE LEADS, because everything below is read through it. Both
          denominators, because they answer different questions and neither may stand in for the
          other. */}
      <p className="ht-callout coverage-completeness">
        <strong>
          {sum.describedExercises} of {sum.weekExercises} exercises described
          ({sum.describedSets} of {sum.weekSets} sets).
        </strong>{" "}
        {sum.provisional > 0
          ? `${sum.provisional} cells cannot be judged yet — an exercise nobody has described and an
             exercise that trains nothing look identical, so neither is called a hole.`
          : "Every exercise in the week is described, so an empty cell here is a real gap."}
        {state.hiddenSkipped > 0 && ` ${state.hiddenSkipped} hidden assignment(s) excluded.`}
      </p>

      <ul className="coverage-counts ht-caption">
        <li><strong>{sum.covered}</strong> covered</li>
        <li><strong>{sum.thin}</strong> thin</li>
        <li><strong>{sum.zeroAccidental}</strong> nothing trains this</li>
        <li><strong>{sum.provisional}</strong> not yet known</li>
        <li><strong>{sum.noPrimeMover}</strong> nothing drives — helpers only</li>
      </ul>

      {/* ⚠️ THE BUTTON IS ITS OWN BLOCK, NOT INLINE IN THE SENTENCE. Rendered inline the text
          wrapped around it and the paragraph read as broken — a defect invisible in source and
          obvious in a screenshot, which is the third time this project has paid for that. */}
      <p className="ht-caption coverage-note">
        Showing {shownReadings.length} of {CELL_COUNT} cells
        {!auditNarrows
          ? " — every one of them, because the only exercises anyone has described are the ones in this week. Until the library reaches past a single week it cannot tell a gap in this athlete from a gap in the library, so nothing is hidden on its authority."
          : showAll
            ? "."
            : " — the rest are ranges that nothing in the described library trains at all, which is a gap in the library rather than in this athlete."}
        {deferralNote}
      </p>
      {/* ⚠️ THE TOGGLE IS HIDDEN WHEN IT WOULD DO NOTHING. Offered while the audit set cannot
          narrow anything, it would promise a filter that either hides nothing or hides every
          finding — a control whose label is false is worse than no control. */}
      {auditNarrows && (
        <p className="coverage-actions">
          <button type="button" className="ht-button ht-button-quiet coverage-toggle"
                  onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show only what the library can fill" : `Show all ${CELL_COUNT} cells`}
          </button>
        </p>
      )}

      {groups.map((g) => (
        <div key={g.joint} className="coverage-group">
          <h3 className="ht-overline coverage-joint">{g.label}</h3>
          <div className="coverage-table-scroll">
            <table className="coverage-table">
              <caption className="ht-visually-hidden">
                {g.label} joint actions, with the weighted sets this week puts into each range
              </caption>
              <thead>
                <tr>
                  <th scope="col">Action &amp; range</th>
                  <th scope="col">Weighted sets</th>
                  <th scope="col">Of which prime</th>
                  <th scope="col">State</th>
                </tr>
              </thead>
              <tbody>
                {g.readings.map((r) => (
                  <tr key={r.key} className={`coverage-row coverage-${r.state}`}>
                    <th scope="row" className="ht-body">
                      {r.action.actionLabel}
                      <span className="coverage-zone ht-caption"> · {r.zone}</span>
                    </th>
                    <td className="coverage-num">{fmt(r.weightedSets)}</td>
                    <td className="coverage-num">{fmt(r.primeWeighted)}</td>
                    <td className="ht-caption">
                      {stateLabel(r.state)}
                      {r.weightedSets > 0 && r.primeWeighted === 0 && " · nothing drives it"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </section>
  );
}

function fmt(n: number): string {
  return n === 0 ? "—" : (Number.isInteger(n) ? String(n) : n.toFixed(2));
}
